import {
  AfterViewInit,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { FormControlHintType } from './form-control-hint-type.enum';
import { FormControlType } from './form-control-type.model';
import { BaseFormField, FormField } from './form-field.model';

/* eslint-disable no-underscore-dangle */
@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export abstract class AbstractFormFieldComponent<T extends BaseFormField> implements OnInit, AfterViewInit {
  @ViewChild('inputText') inputText!: ElementRef;

  control = input<UntypedFormControl>();
  field = input<T>();

  FormControlType = FormControlType;
  FormControlHintType = FormControlHintType;

  defaultDebounceTime = 300;
  required: WritableSignal<boolean> = signal(false);

  protected readonly injector: Injector = inject(Injector);
  protected readonly destroyRef: DestroyRef = inject(DestroyRef);

  ngOnInit() {
    const field = this.field();
    if (field) {
      this.initRequiredEffect();
      this.initDisabledEffect();
      this.initInitialValueEffect();
      this.onValueChangesInit();
    }
  }

  onValueChangesInit(): void {
    const control = this.control();
    const field = this.field();
    if (control && field) {
      control.valueChanges
        .pipe(
          debounceTime(field.debounceTime?.() ?? this.defaultDebounceTime),
          tap((value) => field.valueChange?.(value)),
          distinctUntilChanged(),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    }
  }

  ngAfterViewInit() {
    let isFieldReady = false;
    effect(
      () => {
        const field = this.field();
        if (field && !isFieldReady) {
          isFieldReady = true;
          field?.onReady?.({ ref: this.inputText });
        }
      },
      { injector: this.injector }
    );
  }

  initInitialValueEffect() {
    effect(
      () => {
        const field = this.field();
        const control = this.control();
        if (field?.initialValue) {
          const initialValue = field.initialValue();
          control?.setValue(initialValue);
        }
      },
      { injector: this.injector }
    );
  }

  private initDisabledEffect() {
    effect(
      () => {
        const field = this.field();
        const control = this.control();
        const disabled = field?.disabled;
        if (disabled && (!(field as unknown as FormField)?.disableOnlyOptions || !(field as unknown as FormField)?.disableOnlyOptions?.())) {
          if (disabled()) {
            control?.disable?.();
          } else {
            control?.enable?.();
          }
        }
      },
      { injector: this.injector }
    );
  }

  private initRequiredEffect() {
    effect(
      () => {
        const field = this.field();
        const control = this.control();
        const required = field?.required;
        this.required.set(false);
        if (required) {
          if (required()) {
            // eslint-disable-next-line no-underscore-dangle
            this.required.set(true);
            control?.addValidators?.(Validators.required);
          } else {
            control?.removeValidators?.(Validators.required);
          }
          control?.updateValueAndValidity?.();
        }
      },
      { injector: this.injector }
    );
  }
}
